import { Component, Inject, Vue, Watch } from "vue-property-decorator";
import ModalTemplate from "../../../../shared/components/ModalTemplate/ModalTemplate.vue";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import {
  COLLECTIONS_NAME,
  FORM_CONSTANTS,
  FORM_MESSAGES,
  FORM_VALIDATORS,
  LOCAL_STORAGE,
  PHONE_TRANSLATIONS
} from "@/shared/constants/FormConstants";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { Http } from "@status/codes";
import { Response } from "@/shared/models/Response";
import { CampusService } from "../../services/CampusService";
import { CampusModel } from "../../models/CampusModel";
import {
  CAMPUS_ACCESS,
  CAMPUS_CONSTANTS
} from "../../constants/CampusConstants";
import VuePhoneNumberInput from "vue-phone-number-input";
import { PhonePayload } from "@/modules/InfoGroup/models/PhonePayload";
import { AxiosError } from "axios";
import { Utils } from "@/shared/utils/utils";
import { CampusForms } from "../../models/CampusForms.Model";

@Component({
  name: "SaveCampus",
  components: {
    ModalTemplate,
    VuePhoneNumberInput
  }
})
export default class SaveCampus extends Vue {
  @Inject() campusService!: CampusService;

  cardClass = STYLE_CLASSES.CARD;
  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  switchClass = STYLE_CLASSES.SWITCH;
  iconBtnClass = STYLE_CLASSES.ICON_BTN;

  pageTitle = CAMPUS_CONSTANTS.PAGE_CREATE_TITLE;
  CAMPUS_NAME = CAMPUS_CONSTANTS.LABELS.NAME;
  CAMPUS_ADDRESS = CAMPUS_CONSTANTS.LABELS.ADDRESS;
  CAMPUS_STATE = FORM_CONSTANTS.LABEL.STATE;
  SAVE_BTN = FORM_CONSTANTS.BUTTON.SAVE;
  CANCEL_BTN = FORM_CONSTANTS.BUTTON.CANCEL;

  requiredField = [
    FORM_VALIDATORS.REQUIRED_FIELD,
    FORM_VALIDATORS.NOT_BLANK_SPACES_VALIDATOR
  ];
  countriesPreferredList = FORM_CONSTANTS.PREFERRED_COUNTRIES;
  translations = PHONE_TRANSLATIONS;

  campusForm = false;
  state = true;
  campusId = "";
  isEditing = false;
  modalDialog = false;
  modalText = "";
  modalTitle = "";
  campus!: CampusModel;
  defaultCountry = "";
  errorsName: string[] = [];

  campusFormData: CampusForms = {
    campusName: "",
    campusAddress: "",
    campusPhone: "",
    campusCountryCode: "",
    validPhone: false,
    state: true
  };

  editComparationStr = "";
  invalidEdit = false;

  mounted() {
    this.defaultCountry = localStorage.getItem(LOCAL_STORAGE.COUNTRY) || "";
    if (this.$route.params.id) {
      this.campusId = this.$route.params.id;
      this.getCampusById();
      this.pageTitle = CAMPUS_CONSTANTS.PAGE_EDIT_TITLE;
      this.isEditing = true;
    }
  }

  @Watch("campusFormData", { deep: true })
  checkEdit() {
    if (this.isEditing) {
      this.invalidEdit =
        JSON.stringify(this.campusFormData) === this.editComparationStr;
    }
    return;
  }

  async getCampusById() {
    try {
      const response = await this.campusService.getCampusById(this.campusId);

      if (response.code === Http.Found && response.data) {
        this.campus = response.data;
        this.fillCampusForm();
      } else {
        Snackbar.popInfo(response.message);
      }
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST + CAMPUS_ACCESS.GET);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  fillCampusForm() {
    this.campusFormData.campusName = this.campus.name;
    this.campusFormData.campusAddress = this.campus.address;
    this.campusFormData.campusPhone = this.campus.phone;
    this.campusFormData.campusCountryCode = this.campus.countryCode;
    this.campusFormData.validPhone = true;
    this.campusFormData.state =
      this.campus.state === FORM_CONSTANTS.STATE.ACTIVE ? true : false;

    this.editComparationStr = JSON.stringify(this.campusFormData);
  }

  confirmDialog(isSave: boolean) {
    if (isSave) {
      this.modalTitle = FORM_MESSAGES.SAVE_MODAL.TITLE;
      this.modalText = FORM_MESSAGES.SAVE_MODAL.MESSAGE;
    } else {
      this.modalTitle = FORM_MESSAGES.CANCEL_MODAL.TITLE;
      this.modalText = FORM_MESSAGES.CANCEL_MODAL.MESSAGE;
    }

    this.modalDialog = true;
  }

  async handleModalAccept() {
    this.modalDialog = false;

    if (this.modalTitle === FORM_MESSAGES.SAVE_MODAL.TITLE) {
      await this.saveCampus();
    } else {
      this.$router.go(-1);
    }
  }

  handleModalCancel() {
    this.modalDialog = false;
  }

  onUpdate(payload: PhonePayload) {
    this.campusFormData.campusCountryCode = payload.countryCode;
    this.campusFormData.campusPhone = payload.formattedNumber;
    this.campusFormData.validPhone = payload.isValid ?? false;
  }

  async saveCampus() {
    try {
      const campusToSave: CampusModel = {
        name: this.campusFormData.campusName,
        address: this.campusFormData.campusAddress,
        phone: this.campusFormData.campusPhone
          ? this.campusFormData.campusPhone.replace(/\s/g, "")
          : "",
        countryCode:
          this.campusFormData.campusCountryCode ?? this.defaultCountry,
        state: this.campusFormData.state
          ? FORM_CONSTANTS.STATE.ACTIVE
          : FORM_CONSTANTS.STATE.INACTIVE
      };

      let response: Response<string | void>;

      if (this.isEditing) {
        response = await this.campusService.updateCampus(
          this.campusId,
          campusToSave
        );
      } else {
        response = await this.campusService.addCampus(campusToSave);
      }

      if (response.code === Http.Ok) {
        Snackbar.popSuccess(response.message);
        this.$router.push("/campus");
      } else {
        Snackbar.popError(response.message);
      }
    } catch (error) {
      const METHOD = Utils.getMethodHTTP(
        error as AxiosError
      ) as keyof typeof CAMPUS_ACCESS;
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(
          FORM_MESSAGES.FORBIDDEN_REQUEST + CAMPUS_ACCESS[METHOD]
        );
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async validateUniqueName() {
    this.errorsName = await Utils.checkUniqueName(
      COLLECTIONS_NAME.CAMPUS,
      this.campusFormData.campusName
    );
  }
}
